/**
 * RFQ Lock Utility
 * =================
 * 
 * Manages concurrent access locking for RFQ editing.
 * Prevents multiple users from the same company editing the same RFQ simultaneously.
 * 
 * Features:
 * - Lock acquisition with automatic heartbeat
 * - Lock release on page unload
 * - Admin bypass (role='admin' users are never blocked)
 * - Debounced release for SPA navigation (prevent lock loss when switching tabs)
 */

import { API_ENDPOINTS } from './config';

// Lock configuration
const HEARTBEAT_INTERVAL_MS = 30000; // 30 seconds
const RELEASE_DELAY_MS = 1000; // 1 second grace period for navigation

// Store active heartbeat intervals
const activeHeartbeats: Map<string, NodeJS.Timeout> = new Map();

// Store pending release timeout
let releaseTimeout: NodeJS.Timeout | null = null;

export interface LockInfo {
    user_id: string;
    user_name: string;
    locked_at: string;
    expires_at?: string;
    is_own_lock?: boolean;
}

export interface LockCheckResult {
    success: boolean;
    locked: boolean;
    lock_info: LockInfo | null;
    is_admin: boolean;
    error?: string;
}

export interface LockAcquireResult {
    success: boolean;
    message?: string;
    expires_at?: string;
    lock_info?: LockInfo;
    error?: string;
}

/**
 * Check if an RFQ is locked by another user
 */
export async function checkRfqLock(requestId: string, token: string): Promise<LockCheckResult> {
    try {
        const response = await fetch(API_ENDPOINTS.RFQ_LOCK(requestId), {
            method: 'GET',
            headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json'
            }
        });

        const data = await response.json();
        return data;
    } catch (error: any) {
        console.error('Error checking RFQ lock:', error);
        return {
            success: false,
            locked: false,
            lock_info: null,
            is_admin: false,
            error: error.message
        };
    }
}

/**
 * Acquire lock on an RFQ
 * Starts automatic heartbeat to keep lock alive
 */
export async function acquireRfqLock(requestId: string, token: string): Promise<LockAcquireResult> {
    try {
        const response = await fetch(API_ENDPOINTS.RFQ_LOCK(requestId), {
            method: 'POST',
            headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json'
            }
        });

        const data = await response.json();

        if (response.status === 423) {
            // Locked by another user
            return {
                success: false,
                lock_info: data.lock_info,
                error: data.error || 'RFQ is locked by another user'
            };
        }

        if (data.success) {
            // Start heartbeat to keep lock alive
            startHeartbeat(requestId, token);
        }

        return data;
    } catch (error: any) {
        console.error('Error acquiring RFQ lock:', error);
        return {
            success: false,
            error: error.message
        };
    }
}

/**
 * Release lock on an RFQ
 * Stops the heartbeat interval
 */
export async function releaseRfqLock(requestId: string, token: string): Promise<boolean> {
    try {
        // Stop heartbeat first
        stopHeartbeat(requestId);

        const response = await fetch(API_ENDPOINTS.RFQ_LOCK(requestId), {
            method: 'DELETE',
            headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json'
            }
        });

        const data = await response.json();
        return data.success;
    } catch (error: any) {
        console.error('Error releasing RFQ lock:', error);
        return false;
    }
}

/**
 * Schedule lock release with a delay (checking for navigation)
 * Useful for SPA navigation between lock-protected pages
 */
export function scheduleReleaseLock(requestId: string, token: string): void {
    if (releaseTimeout) {
        clearTimeout(releaseTimeout);
    }

    // Stop heartbeat immediately on this component (it will be restarted by next component if navigating)
    stopHeartbeat(requestId);

    console.log(`⏳ Scheduling lock release for ${requestId} in ${RELEASE_DELAY_MS}ms...`);
    releaseTimeout = setTimeout(() => {
        console.log(`🔓 Executing scheduled lock release for ${requestId}`);
        releaseRfqLock(requestId, token);
        releaseTimeout = null;
    }, RELEASE_DELAY_MS);
}

/**
 * Cancel any pending lock release
 * Call this when mounting a component that needs the lock
 * @returns true if a release was cancelled
 */
export function cancelPendingRelease(): boolean {
    if (releaseTimeout) {
        clearTimeout(releaseTimeout);
        releaseTimeout = null;
        console.log('🛑 Cancelled pending lock release (Navigation detected)');
        return true;
    }
    return false;
}

/**
 * Start heartbeat to keep lock alive
 * Should be called after acquiring a lock
 */
export function startHeartbeat(requestId: string, token: string): void {
    // Stop any existing heartbeat for this request
    stopHeartbeat(requestId);

    console.log(`💓 Starting heartbeat for ${requestId}`);
    const intervalId = setInterval(async () => {
        try {
            const response = await fetch(API_ENDPOINTS.RFQ_LOCK_HEARTBEAT(requestId), {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                }
            });

            if (response.status === 410) {
                // Lock expired, stop heartbeat
                console.warn(`RFQ lock expired for request ${requestId}`);
                stopHeartbeat(requestId);
            }
        } catch (error) {
            console.error('Error sending heartbeat:', error);
        }
    }, HEARTBEAT_INTERVAL_MS);

    activeHeartbeats.set(requestId, intervalId);
}

/**
 * Stop heartbeat for a request
 */
export function stopHeartbeat(requestId: string): void {
    const intervalId = activeHeartbeats.get(requestId);
    if (intervalId) {
        clearInterval(intervalId);
        activeHeartbeats.delete(requestId);
    }
}

/**
 * Stop all active heartbeats
 * Useful for cleanup on logout or app unmount
 */
export function stopAllHeartbeats(): void {
    activeHeartbeats.forEach((intervalId, requestId) => {
        clearInterval(intervalId);
    });
    activeHeartbeats.clear();
}

/**
 * Format lock time as relative string (e.g., "5 minutes ago")
 */
export function formatLockTime(lockedAt: string): string {
    // Backend sends naive UTC string (e.g., "2023-10-27T10:00:00")
    // If we parse this directly, browser assumes local time.
    // We must append 'Z' to treat it as UTC.
    const dateStr = lockedAt.endsWith('Z') ? lockedAt : `${lockedAt}Z`;
    const lockDate = new Date(dateStr);
    const now = new Date();
    const diffMs = now.getTime() - lockDate.getTime();
    const diffMinutes = Math.floor(diffMs / 60000);

    if (diffMinutes < 1) {
        return 'just now';
    } else if (diffMinutes === 1) {
        return '1 minute ago';
    } else if (diffMinutes < 60) {
        return `${diffMinutes} minutes ago`;
    } else {
        const diffHours = Math.floor(diffMinutes / 60);
        return diffHours === 1 ? '1 hour ago' : `${diffHours} hours ago`;
    }
}

/**
 * Setup beforeunload handler to release lock when user closes/navigates away
 */
export function setupLockReleaseOnUnload(requestId: string, token: string): () => void {
    const handler = () => {
        // Use sendBeacon for reliability on page unload
        const url = API_ENDPOINTS.RFQ_LOCK(requestId);

        // sendBeacon doesn't support DELETE, so we'll try fetch with keepalive
        // Note: This may not always work, but it's the best effort
        try {
            fetch(url, {
                method: 'DELETE',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                keepalive: true
            });
        } catch (error) {
            // Ignore errors on unload
        }
    };

    window.addEventListener('beforeunload', handler);

    // Return cleanup function
    return () => {
        window.removeEventListener('beforeunload', handler);
    };
}
